/**
 * Colours as lights and their tiles speak of them (docs/specs/light-tile.md): `#rrggbb`, hue and
 * saturation as a hub reports them, and white by its colour temperature.
 */

export interface Rgb {
    readonly r: number;
    readonly g: number;
    readonly b: number;
}

/** A hue (0–360°), saturation and value (0–1 each). */
export interface Hsv {
    readonly h: number;
    readonly s: number;
    readonly v: number;
}

const HEX = /^#([0-9a-f]{6})$/i;
const clamp = (value: number, low: number, high: number) : number => Math.min(high, Math.max(low, value));
const byte = (value: number) : string => Math.round(clamp(value, 0, 255)).toString(16).padStart(2, '0');

/** `#rrggbb` (any case), or undefined for anything else. */
export function hexToRgb(hex: string) : Rgb | undefined {
    const match = HEX.exec(hex.trim());
    if (!match) {
        return undefined;
    }
    const value = Number.parseInt(match[1]!, 16);

    return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

export const rgbToHex = ({ r, g, b }: Rgb) : string => `#${byte(r)}${byte(g)}${byte(b)}`;

export function hsvToHex({ h, s, v }: Hsv) : string {
    const hue = (((h % 360) + 360) % 360) / 60;
    const chroma = clamp(v, 0, 1) * clamp(s, 0, 1);
    const x = chroma * (1 - Math.abs((hue % 2) - 1));
    const [r, g, b] = hue < 1 ? [chroma, x, 0] : hue < 2 ? [x, chroma, 0] : hue < 3 ? [0, chroma, x] : hue < 4 ? [0, x, chroma] : hue < 5 ? [x, 0, chroma] : [chroma, 0, x];
    const m = clamp(v, 0, 1) - chroma;

    return rgbToHex({ r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 });
}

/** The hue, saturation and value of `#rrggbb`; undefined for anything else. */
export function hexToHsv(hex: string) : Hsv | undefined {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return undefined;
    }
    const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255];
    const max = Math.max(r, g, b);
    const delta = max - Math.min(r, g, b);
    let h = 0;
    if (delta > 0) {
        h = max === r ? 60 * (((g - b) / delta) % 6) : max === g ? 60 * ((b - r) / delta + 2) : 60 * ((r - g) / delta + 4);
    }

    return { h: (h + 360) % 360, s: max === 0 ? 0 : delta / max, v: max };
}

/**
 * The colour of white light at `kelvin` (1000–40000 K), from warm orange to cool blue, as an
 * approximation of the black body's colour (Tanner Helland's), good enough to show a light's tone.
 */
export function kelvinToHex(kelvin: number) : string {
    const t = clamp(kelvin, 1000, 40000) / 100;
    const r = t <= 66 ? 255 : 329.698727446 * (t - 60) ** -0.1332047592;
    const g = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * (t - 60) ** -0.0755148492;
    const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;

    return rgbToHex({ r, g, b });
}
